"use client";

import { motion, AnimatePresence } from "motion/react";
import {
  ArrowUpRight,
  CheckCircle2,
  Copy,
  FileStack,
  FolderInput,
  Hourglass,
  Inbox,
  Layers3,
  Search,
  Trash2,
  ShieldCheck,
  AlertTriangle,
  UsersRound,
  ListChecks,
} from "lucide-react";
import type { Dispatch, FC, SetStateAction } from "react";
import React, { useMemo, useState } from "react";

import RenderIf from "@/app/_module/components/RenderIf";
import { Badge } from "@/app/_module/components/ui/badge";
import ImportErrorsDialog from "./ImportErrorsDialog";
import { Button } from "@/app/_module/components/ui/button";
import { DialogFooter } from "@/app/_module/components/ui/dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter as DialogFooterUI,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import { Input } from "@/app/_module/components/ui/input";
import { ScrollArea } from "@/app/_module/components/ui/scroll-area";
import { Skeleton } from "@/app/_module/components/ui/skeleton";
import { formatFriendlyDate } from "@/app/_module/utils/formatters";

import {
  useActivateLeadsImportById,
  useDeleteImport,
  useGetImportDeletionPreview,
  useGetLeadsImportList,
  useMoveImportToStage,
  useBulkDeleteImports,
} from "../hooks/leads-import-queries";
import { successToast } from "@/app/_module/services/helper";
import { usePipelineStages } from "../hooks/queries";

import ImportedLeadListDetails from "./ImportedLeadListDetails";

interface IImportedLeadsList {
  isViewingDetails: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setIsViewingDetails: Dispatch<SetStateAction<boolean>>;
}

type FilterKey = "all" | "pending" | "activated" | "failed";

type ImportedList = {
  id: string;
  fileName: string;
  createdAt: string | Date;
  resultCounts: { success: number; failed: number; created?: number; skipped?: number; assigned?: number; activeCreated?: number };
  assignedAgents?: { agentId: string; name: string; count: number }[];
  status?: string;
  activatedAt?: string | null;
};

function deriveStatus(list: ImportedList): "activated" | "pending" | "failed" {
  const { success, failed } = list.resultCounts || { success: 0, failed: 0 };
  const raw = (list.status || "").toString().toLowerCase();
  if (list.activatedAt || raw === "activated" || raw === "completed" || raw === "active") {
    return "activated";
  }
  if (success === 0 && failed > 0) return "failed";
  return "pending";
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toLocaleString();
}

const ImportedLeadsList: FC<IImportedLeadsList> = function (prop) {
  const { setOpen, isViewingDetails, setIsViewingDetails } = prop;

  const [leadListId, setLeadListId] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<FilterKey>("all");

  const { data: importedLeadsList, isLoading } = useGetLeadsImportList({
    refetchInterval: !isViewingDetails ? 30000 : false,
  });
  const { mutate, isPending, variables } = useActivateLeadsImportById();

  // Delete-upload confirmation. deleteTarget holds the import being confirmed.
  const [deleteTarget, setDeleteTarget] = useState<ImportedList | null>(null);
  const { data: deletePreview, isLoading: previewLoading } =
    useGetImportDeletionPreview(deleteTarget?.id);
  const { mutate: deleteImport, isPending: isDeleting } = useDeleteImport();

  function handleConfirmDelete() {
    if (!deleteTarget) return;
    deleteImport(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  }

  // ── Bulk selection + background bulk delete ─────────────────────────────
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [errorsFor, setErrorsFor] = useState<{ id: string; fileName?: string; failed: number } | null>(null);
  const { mutate: startBulkDelete, isPending: isStartingBulk } = useBulkDeleteImports();

  const toggleSelected = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const exitSelectMode = () => { setSelectMode(false); setSelectedIds(new Set()); setShowBulkConfirm(false); };

  function handleConfirmBulkDelete() {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    startBulkDelete(ids, {
      onSuccess: (res: any) => {
        successToast(`Deleting ${res?.totalLists ?? ids.length} lists in the background — we'll notify you when it's done.`);
        exitSelectMode();
      },
    });
  }

  // Move-to-pipeline. moveTarget holds the import being moved; selectedStage is
  // the chosen pipeline stage name. The deletion preview doubles as the move
  // preview (its `deletable` count = non-worked created leads = what moves).
  const [moveTarget, setMoveTarget] = useState<ImportedList | null>(null);
  const [selectedStage, setSelectedStage] = useState<string>("");
  const { data: movePreview, isLoading: movePreviewLoading } =
    useGetImportDeletionPreview(moveTarget?.id);
  const { data: pipelineStages } = usePipelineStages();
  const { mutate: moveImportToStage, isPending: isMoving } = useMoveImportToStage();

  function closeMove() {
    setMoveTarget(null);
    setSelectedStage("");
  }

  function handleConfirmMove() {
    if (!moveTarget || !selectedStage) return;
    moveImportToStage(
      { importId: moveTarget.id, newStatus: selectedStage },
      { onSuccess: () => closeMove() },
    );
  }

  const lists: ImportedList[] = useMemo(
    () => ((importedLeadsList as ImportedList[] | undefined) ?? []),
    [importedLeadsList],
  );

  const summary = useMemo(() => {
    let totalLeads = 0;
    let pending = 0;
    let activated = 0;
    for (const l of lists) {
      const s = l.resultCounts?.success ?? 0;
      const f = l.resultCounts?.failed ?? 0;
      totalLeads += s + f;
      const st = deriveStatus(l);
      if (st === "activated") activated += 1;
      else if (st === "pending") pending += 1;
    }
    return { totalLists: lists.length, totalLeads, pending, activated };
  }, [lists]);

  const filteredLists = useMemo(() => {
    const q = query.trim().toLowerCase();
    return lists.filter((l) => {
      const st = deriveStatus(l);
      if (filter !== "all" && filter !== st) return false;
      if (q && !(l.fileName || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [lists, query, filter]);

  function handleRowClick(id: string) {
    setLeadListId(id);
    setIsViewingDetails(true);
  }

  function handleActivateImport(importId: string) {
    mutate({ importId, param: { bypassImmediateCheck: true } });
  }

  const handleCancel = () => {
    setOpen(false);
    setIsViewingDetails(false);
  };

  const activatingId = isPending ? (variables as { importId?: string } | undefined)?.importId : undefined;

  const filterTabs: { key: FilterKey; label: string; count: number }[] = [
    { key: "all", label: "All", count: summary.totalLists },
    { key: "pending", label: "Pending", count: summary.pending },
    { key: "activated", label: "Activated", count: summary.activated },
    { key: "failed", label: "Failed", count: lists.filter((l) => deriveStatus(l) === "failed").length },
  ];

  // ⚠️ Hooks BEFORE the early return: this useMemo used to live below the
  // isViewingDetails return, so drilling into a list rendered fewer hooks
  // than the previous pass and crashed the route ("Rendered fewer hooks
  // than expected"). Every hook must run on every render path.
  const latestWithErrors = useMemo(
    () =>
      [...lists]
        .filter((l) => (l.resultCounts?.failed ?? 0) > 0)
        .sort((a, b) => new Date(b.createdAt as string).getTime() - new Date(a.createdAt as string).getTime())[0],
    [lists],
  );

  if (isViewingDetails) {
    return (
      <React.Fragment>
        <ScrollArea className="h-full overflow-y-scroll w-full">
          <ImportedLeadListDetails leadListId={leadListId} />
        </ScrollArea>
        <DialogFooter>
          <Button onClick={handleCancel} variant="outline">Close</Button>
        </DialogFooter>
      </React.Fragment>
    );
  }

  // Most recent import that lost rows. Surfaced at the TOP because the whole
  // problem was discoverability: someone uploads 15,000 leads, 103 never
  // arrive, and the only signal was a small red count part-way down a list.
  // People went looking in the LEAD list for records that never became leads.

  return (
    <React.Fragment>
      {/* Most recent import that dropped rows — one click to see exactly which. */}
      {latestWithErrors && (
        <div className="mb-3 flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
          <AlertTriangle className="h-4 w-4 shrink-0 text-red-600" />
          <div className="min-w-0 flex-1 text-[13px] leading-snug">
            <span className="font-medium text-red-800">
              {formatCount(latestWithErrors.resultCounts?.failed ?? 0)} row
              {(latestWithErrors.resultCounts?.failed ?? 0) === 1 ? "" : "s"} didn&apos;t import
            </span>
            <span className="text-red-700">
              {" "}from <span className="font-medium">{latestWithErrors.fileName || "your last upload"}</span>.
              They never became leads, so they won&apos;t appear in your lead list.
            </span>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="h-7 shrink-0 border-red-300 bg-white text-xs text-red-700 hover:bg-red-100"
            onClick={() =>
              setErrorsFor({
                id: latestWithErrors.id,
                fileName: latestWithErrors.fileName,
                failed: latestWithErrors.resultCounts?.failed ?? 0,
              })
            }
          >
            See what failed
          </Button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 pb-4">
        <StatCard
          icon={<Layers3 className="h-4 w-4" />}
          label="Total lists"
          value={summary.totalLists}
        />
        <StatCard
          icon={<FileStack className="h-4 w-4" />}
          label="Leads processed"
          value={summary.totalLeads}
        />
        <StatCard
          icon={<Hourglass className="h-4 w-4" />}
          label="Pending activation"
          value={summary.pending}
          highlight={summary.pending > 0}
        />
      </div>

      {/* Search + filters */}
      <div className="flex flex-col gap-3 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            className="pl-9 h-9"
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by file name..."
            value={query}
          />
        </div>

        <div className="flex items-center gap-1 rounded-lg border bg-gray-50 p-0.5">
          {filterTabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setFilter(t.key)}
              type="button"
              className={[
                "relative px-3 py-1 rounded-md text-xs font-medium transition-colors",
                filter === t.key
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-900",
              ].join(" ")}
            >
              {t.label}
              <span className={[
                "ml-1.5 tabular-nums text-[10px]",
                filter === t.key ? "text-gray-500" : "text-gray-400",
              ].join(" ")}>
                {t.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Bulk-select bar */}
      <div className="mb-3 min-h-[2.25rem]">
        {!selectMode ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSelectMode(true)}
            className="gap-1.5"
          >
            <ListChecks className="h-4 w-4" /> Select lists
          </Button>
        ) : (
          (() => {
            const allSelected = filteredLists.length > 0 && filteredLists.every((l) => selectedIds.has(l.id));
            return (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setSelectedIds(allSelected ? new Set() : new Set(filteredLists.map((l) => l.id)))}
                    className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center ${allSelected ? "bg-primary border-primary" : "border-gray-300 bg-white"}`}>
                      {allSelected && <CheckCircle2 className="w-3 h-3 text-white" />}
                    </span>
                    Select all
                  </button>
                  <span className="text-sm text-gray-500 tabular-nums">
                    {selectedIds.size} of {filteredLists.length} selected
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={exitSelectMode}>Cancel</Button>
                  <Button
                    size="sm"
                    onClick={() => setShowBulkConfirm(true)}
                    disabled={selectedIds.size === 0}
                    className="gap-1.5 bg-red-600 hover:bg-red-700 text-white"
                  >
                    <Trash2 className="h-4 w-4" /> Delete {selectedIds.size} list{selectedIds.size === 1 ? "" : "s"}
                  </Button>
                </div>
              </div>
            );
          })()
        )}
      </div>

      {/* Entries */}
      <ScrollArea className="h-full overflow-y-scroll w-full">
        <div>
          {/* Column header */}
          <div className="hidden md:grid grid-cols-[1fr_150px_200px_140px] gap-4 items-center pb-2 border-b">
            <span className="text-xs font-medium text-gray-500">File name</span>
            <span className="text-xs font-medium text-gray-500">Imported</span>
            <span className="text-xs font-medium text-gray-500">Results</span>
            <span className="text-xs font-medium text-gray-500 text-right">Action</span>
          </div>

          {/* Loading */}
          <RenderIf condition={isLoading}>
            <div className="py-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="grid grid-cols-[1fr_150px_200px_140px] gap-4 items-center py-4 border-b"
                >
                  <Skeleton className="h-5 w-48" />
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-2 w-full" />
                  <Skeleton className="h-7 w-24 ml-auto" />
                </div>
              ))}
            </div>
          </RenderIf>

          {/* Empty */}
          <RenderIf condition={!isLoading && lists.length === 0}>
            <EmptyState />
          </RenderIf>

          {/* Empty filtered */}
          <RenderIf condition={!isLoading && lists.length > 0 && filteredLists.length === 0}>
            <div className="py-16 text-center">
              <p className="text-sm text-gray-500">
                No lists match <span className="font-medium text-gray-900">&ldquo;{query}&rdquo;</span>
                {filter !== "all" && <> in <span className="font-medium text-gray-900">{filter}</span></>}.
              </p>
            </div>
          </RenderIf>

          {/* Entries */}
          <RenderIf condition={!isLoading && filteredLists.length > 0}>
            <ul>
              <AnimatePresence initial={false}>
                {filteredLists.map((list, index) => {
                  const status = deriveStatus(list);
                  const success = list.resultCounts?.success ?? 0;
                  const failed = list.resultCounts?.failed ?? 0;
                  // Surface why "only X went through": duplicates (skipped —
                  // already in the CRM) vs errors (failed — bad/missing email,
                  // phone, validation, etc.) vs genuinely new (created).
                  const skipped = list.resultCounts?.skipped ?? 0;
                  const newCreated = list.resultCounts?.created ?? success;
                  const assigned = list.resultCounts?.assigned ?? 0;
                  const activeCreated = list.resultCounts?.activeCreated ?? 0;
                  const assignedAgents = list.assignedAgents ?? [];
                  const total = success + failed;
                  const successPct = total > 0 ? (success / total) * 100 : 0;
                  const rowIsActivating = activatingId === list.id;

                  return (
                    <motion.li
                      key={list.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.22, delay: Math.min(index * 0.02, 0.2), ease: [0.22, 1, 0.36, 1] }}
                      className="group relative"
                    >
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => selectMode ? toggleSelected(list.id) : handleRowClick(list.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            selectMode ? toggleSelected(list.id) : handleRowClick(list.id);
                          }
                        }}
                        className={`relative w-full text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1 rounded ${selectMode && selectedIds.has(list.id) ? "bg-red-50" : ""}`}
                      >
                        {/* Left accent on hover */}
                        <span
                          aria-hidden
                          className="absolute left-0 top-2 bottom-2 w-[2px] origin-top scale-y-0 bg-primary transition-transform duration-300 ease-out group-hover:scale-y-100"
                        />

                        <div className="grid grid-cols-[1fr_150px_200px_140px] gap-4 items-center py-4 border-b pl-3 pr-2 group-hover:bg-gray-50 transition-colors">
                          {/* File name + status */}
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              {selectMode && (
                                <span
                                  className={`w-4 h-4 flex-shrink-0 rounded border flex items-center justify-center ${selectedIds.has(list.id) ? "bg-red-600 border-red-600" : "border-gray-300 bg-white"}`}
                                >
                                  {selectedIds.has(list.id) && <CheckCircle2 className="w-3 h-3 text-white" />}
                                </span>
                              )}
                              <h3 className="truncate text-sm font-medium text-gray-900">
                                {list.fileName || "Untitled import"}
                              </h3>
                              <StatusBadge status={status} />
                            </div>
                            {/* How many of this list's leads are assigned to agents
                                (mirrors the lead-assignment list, so owners don't
                                have to cross-reference). */}
                            {activeCreated > 0 && (
                              <div className="mt-1 flex items-center gap-1.5 text-[11px] flex-wrap">
                                {assigned > 0 ? (
                                  <>
                                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-1.5 py-0.5 font-medium text-blue-700" title={`${assigned.toLocaleString()} of ${activeCreated.toLocaleString()} active leads are assigned to an agent`}>
                                      <UsersRound className="h-3 w-3" />
                                      {formatCount(assigned)} assigned
                                    </span>
                                    {assignedAgents.length > 0 && (
                                      <span className="text-gray-500" title={assignedAgents.map((a) => `${a.name} (${a.count})`).join(", ")}>
                                        to {assignedAgents.slice(0, 3).map((a) => `${a.name}${assignedAgents.length > 1 ? ` (${a.count})` : ""}`).join(", ")}
                                        {assignedAgents.length > 3 ? ` +${assignedAgents.length - 3} more` : ""}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-gray-400">None assigned yet</span>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Date */}
                          <div className="text-xs text-gray-500">
                            {formatFriendlyDate(list.createdAt as string)}
                          </div>

                          {/* Results */}
                          <div>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-gray-900 tabular-nums">
                                <span className="font-medium">{formatCount(success)}</span>
                                <span className="text-gray-300 mx-1">/</span>
                                <span className="text-gray-400">{formatCount(total)}</span>
                              </span>
                              <span className="text-[10px] text-gray-400 tabular-nums">
                                {total > 0 ? `${successPct.toFixed(0)}%` : "—"}
                              </span>
                            </div>
                            <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden flex">
                              <motion.span
                                className="h-full bg-green-600"
                                initial={{ width: 0 }}
                                animate={{ width: `${successPct}%` }}
                                transition={{ duration: 0.5, delay: 0.08 + index * 0.015, ease: [0.22, 1, 0.36, 1] }}
                              />
                              <motion.span
                                className="h-full bg-red-500"
                                initial={{ width: 0 }}
                                animate={{ width: `${total > 0 ? (failed / total) * 100 : 0}%` }}
                                transition={{ duration: 0.5, delay: 0.12 + index * 0.015, ease: [0.22, 1, 0.36, 1] }}
                              />
                            </div>
                            {/* Breakdown of what happened — new / duplicates / errors.
                                Only shown when something didn't import cleanly. */}
                            {(skipped > 0 || failed > 0) && (
                              <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px] tabular-nums">
                                <span className="text-green-700 font-medium">{formatCount(newCreated)} new</span>
                                {skipped > 0 && (
                                  <>
                                    <span className="text-gray-300">·</span>
                                    <span className="inline-flex items-center gap-1 text-amber-700" title="Already in your CRM — matched an existing lead">
                                      <Copy className="h-2.5 w-2.5" />
                                      {formatCount(skipped)} duplicate{skipped === 1 ? "" : "s"}
                                    </span>
                                  </>
                                )}
                                {failed > 0 && (
                                  <>
                                    <span className="text-gray-300">·</span>
                                    {/* Clickable: the bare count left people
                                        scrolling the lead list hunting for rows
                                        that never became leads at all. */}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setErrorsFor({ id: list.id, fileName: list.fileName, failed });
                                      }}
                                      className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 hover:underline underline-offset-2"
                                      title="See exactly which rows failed and why"
                                    >
                                      <AlertTriangle className="h-2.5 w-2.5" />
                                      {formatCount(failed)} error{failed === 1 ? "" : "s"}
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Action */}
                          <div className="flex items-center justify-end gap-2">
                            <RenderIf condition={status === "activated"}>
                              <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                Activated
                              </span>
                            </RenderIf>

                            <RenderIf condition={status !== "activated"}>
                              <Button
                                className="h-7 px-3 text-xs gap-1.5"
                                disabled={isPending}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleActivateImport(list.id);
                                }}
                                size="sm"
                              >
                                <RenderIf condition={rowIsActivating}>
                                  <span className="inline-block h-3 w-3 rounded-full border-2 border-current border-r-transparent animate-spin" />
                                  Activating...
                                </RenderIf>
                                <RenderIf condition={!rowIsActivating}>
                                  Activate
                                  <ArrowUpRight className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </RenderIf>
                              </Button>
                            </RenderIf>

                            {/* Move this upload's leads to a pipeline stage */}
                            <button
                              type="button"
                              aria-label="Move leads to pipeline"
                              title="Move this upload's leads to a pipeline"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMoveTarget(list);
                              }}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors"
                            >
                              <FolderInput className="h-4 w-4" />
                            </button>

                            {/* Delete this upload (and the leads it created) */}
                            <button
                              type="button"
                              aria-label="Delete upload"
                              title="Delete this upload and its leads"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTarget(list);
                              }}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.li>
                  );
                })}
              </AnimatePresence>
            </ul>
          </RenderIf>
        </div>
      </ScrollArea>

      <DialogFooter className="pt-4 border-t">
        <div className="flex items-center gap-3 mr-auto text-xs text-gray-400">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-green-600" />
            Imported
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
            Failed
          </span>
          <span className="text-gray-300">·</span>
          <span>Auto-refresh 30s</span>
        </div>
        <Button onClick={handleCancel} size="sm" variant="outline">Close</Button>
      </DialogFooter>

      {/* Delete-upload confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o && !isDeleting) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-600">
                <Trash2 className="h-4 w-4" />
              </span>
              Delete this upload?
            </DialogTitle>
            <DialogDescription className="pt-1">
              <span className="font-medium text-gray-900">{deleteTarget?.fileName || "Untitled import"}</span>
              {" "}— this removes the leads this upload created, <span className="font-medium text-gray-900">including any
              you&rsquo;ve assigned to agents</span> (they&rsquo;ll be removed from those agents too). It
              can&rsquo;t undo leads that were already here.
            </DialogDescription>
          </DialogHeader>

          {previewLoading ? (
            <div className="space-y-2 py-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ) : (
            <div className="rounded-lg border bg-gray-50 p-3 text-sm space-y-2">
              {(deletePreview?.deletable ?? 0) > 0 ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 text-gray-700">
                      <Trash2 className="h-4 w-4 text-red-500" /> Will delete
                    </span>
                    <span className="font-semibold tabular-nums text-red-600">
                      {(deletePreview?.deletable ?? 0).toLocaleString()} leads
                    </span>
                  </div>
                  <div className="flex items-start gap-2 text-xs text-green-800 bg-green-50 border border-green-200 rounded-md p-2">
                    <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0 text-green-600" />
                    <span>
                      <span className="font-medium">Your appointments, sold, approved &amp; underwriting leads are saved.</span> Anything
                      worked (sold, approved, in underwriting, or with an appointment) is always kept — never deleted.
                    </span>
                  </div>
                  <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-500" />
                    <span>
                      Any of these leads you assigned to agents will be deleted from them too.
                    </span>
                  </div>
                </>
              ) : !deletePreview?.exceedsLimit && (
                <div className="flex items-start gap-2 text-gray-700">
                  <Trash2 className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
                  <span>This upload has <span className="font-medium">no leads left to remove</span> — deleting just clears the empty list entry.</span>
                </div>
              )}
              {(deletePreview?.protected ?? 0) > 0 && (
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-gray-700">
                    <ShieldCheck className="h-4 w-4 text-green-600" /> Kept (sold / underwriting / appointments)
                  </span>
                  <span className="font-semibold tabular-nums text-green-700">
                    {(deletePreview?.protected ?? 0).toLocaleString()}
                  </span>
                </div>
              )}
              {(deletePreview?.updated ?? 0) > 0 && (
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-gray-700">
                    <ShieldCheck className="h-4 w-4 text-gray-400" /> Kept (already existed, only updated)
                  </span>
                  <span className="font-semibold tabular-nums text-gray-600">
                    {(deletePreview?.updated ?? 0).toLocaleString()}
                  </span>
                </div>
              )}
              {deletePreview?.exceedsLimit ? (
                <div className="flex items-start gap-2 pt-1 text-xs text-red-600 border-t mt-1">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  This upload is over the {(deletePreview?.maxDeletable ?? 25000).toLocaleString()}-at-a-time
                  limit. Ask an admin to remove a list this large.
                </div>
              ) : (
                <div className="flex items-start gap-2 pt-1 text-xs text-gray-500 border-t mt-1">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-500" />
                  Deleted leads go to <span className="font-medium text-gray-700">Recently Deleted</span> and can be restored.
                </div>
              )}
            </div>
          )}

          <DialogFooterUI>
            <Button variant="outline" disabled={isDeleting} onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={
                isDeleting ||
                previewLoading ||
                deletePreview?.exceedsLimit === true
              }
              onClick={handleConfirmDelete}
              className="gap-1.5"
            >
              {isDeleting ? (
                <>
                  <span className="inline-block h-3 w-3 rounded-full border-2 border-current border-r-transparent animate-spin" />
                  Deleting...
                </>
              ) : (deletePreview?.deletable ?? 0) === 0 ? (
                <>
                  <Trash2 className="h-4 w-4" />
                  Remove empty list
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4" />
                  Delete {(deletePreview?.deletable ?? 0).toLocaleString()} leads
                </>
              )}
            </Button>
          </DialogFooterUI>
        </DialogContent>
      </Dialog>

      {/* Move-to-pipeline */}
      <Dialog open={!!moveTarget} onOpenChange={(o) => { if (!o && !isMoving) closeMove(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                <FolderInput className="h-4 w-4" />
              </span>
              Move leads to a pipeline
            </DialogTitle>
            <DialogDescription className="pt-1">
              <span className="font-medium text-gray-900">{moveTarget?.fileName || "Untitled import"}</span>
              {" "}— move the leads this upload created into a pipeline stage. Pre-existing leads
              aren&rsquo;t touched, and worked leads (sold / underwriting / appointments) keep their stage.
            </DialogDescription>
          </DialogHeader>

          {/* Preview counts */}
          {movePreviewLoading ? (
            <div className="space-y-2 py-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ) : (
            <div className="rounded-lg border bg-gray-50 p-3 text-sm space-y-2">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-gray-700">
                  <FolderInput className="h-4 w-4 text-primary" /> Will move
                </span>
                <span className="font-semibold tabular-nums text-primary">
                  {(movePreview?.deletable ?? 0).toLocaleString()} leads
                </span>
              </div>
              {(movePreview?.protected ?? 0) > 0 && (
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-gray-700">
                    <ShieldCheck className="h-4 w-4 text-green-600" /> Kept (sold / underwriting / appointments)
                  </span>
                  <span className="font-semibold tabular-nums text-green-700">
                    {(movePreview?.protected ?? 0).toLocaleString()}
                  </span>
                </div>
              )}
              {movePreview?.exceedsLimit && (
                <div className="flex items-start gap-2 pt-1 text-xs text-red-600 border-t mt-1">
                  <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  This upload is over the {(movePreview?.maxDeletable ?? 25000).toLocaleString()}-at-a-time
                  limit. Ask an admin to move a list this large.
                </div>
              )}
            </div>
          )}

          {/* Stage picker */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-500">Move to</p>
            <div className="grid grid-cols-2 gap-2 max-h-52 overflow-y-auto pr-1">
              {(pipelineStages ?? [])
                .filter((s) => s.isVisible !== false)
                .map((stage) => {
                  const active = selectedStage === stage.name;
                  return (
                    <button
                      key={stage.id}
                      type="button"
                      onClick={() => setSelectedStage(stage.name)}
                      className={[
                        "flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors",
                        active
                          ? "border-primary bg-primary/5 ring-1 ring-primary"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50",
                      ].join(" ")}
                    >
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: stage.color || "#9ca3af" }}
                      />
                      <span className="truncate text-gray-900">{stage.name}</span>
                      {!stage.isSystem && (
                        <span className="ml-auto text-[9px] uppercase tracking-wider text-gray-400">custom</span>
                      )}
                    </button>
                  );
                })}
            </div>
          </div>

          <DialogFooterUI>
            <Button variant="outline" disabled={isMoving} onClick={closeMove}>
              Cancel
            </Button>
            <Button
              disabled={
                isMoving ||
                movePreviewLoading ||
                !selectedStage ||
                (movePreview?.deletable ?? 0) === 0 ||
                movePreview?.exceedsLimit === true
              }
              onClick={handleConfirmMove}
              className="gap-1.5"
            >
              {isMoving ? (
                <>
                  <span className="inline-block h-3 w-3 rounded-full border-2 border-current border-r-transparent animate-spin" />
                  Moving...
                </>
              ) : (
                <>
                  <FolderInput className="h-4 w-4" />
                  Move {(movePreview?.deletable ?? 0).toLocaleString()} {selectedStage ? `to "${selectedStage}"` : "leads"}
                </>
              )}
            </Button>
          </DialogFooterUI>
        </DialogContent>
      </Dialog>

      {/* Bulk-delete confirmation */}
      <Dialog open={showBulkConfirm} onOpenChange={(o) => { if (!o && !isStartingBulk) setShowBulkConfirm(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-600">
                <Trash2 className="h-4 w-4" />
              </span>
              Delete {selectedIds.size} list{selectedIds.size === 1 ? "" : "s"}?
            </DialogTitle>
            <DialogDescription className="pt-1">
              This removes the leads these uploads created, including any you&rsquo;ve assigned to agents. It runs in the
              background so it won&rsquo;t slow anything down — we&rsquo;ll notify you when it&rsquo;s done.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border bg-gray-50 p-3 text-sm space-y-2">
            <div className="flex items-start gap-2 text-xs text-green-800 bg-green-50 border border-green-200 rounded-md p-2">
              <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0 text-green-600" />
              <span><span className="font-medium">Your appointments, sold, approved &amp; underwriting leads are saved.</span> Anything worked is always kept — never deleted. Deleted lists go to <span className="font-medium">Recently Deleted → Lists</span> and can be restored.</span>
            </div>
          </div>
          <DialogFooterUI>
            <Button variant="outline" onClick={() => setShowBulkConfirm(false)} disabled={isStartingBulk}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={handleConfirmBulkDelete} disabled={isStartingBulk}>
              {isStartingBulk ? "Starting…" : `Delete ${selectedIds.size} list${selectedIds.size === 1 ? "" : "s"}`}
            </Button>
          </DialogFooterUI>
        </DialogContent>
      </Dialog>

      {/* Which rows failed, and why — opened from the "N errors" badge. */}
      <ImportErrorsDialog
        open={!!errorsFor}
        onOpenChange={(v) => { if (!v) setErrorsFor(null); }}
        importId={errorsFor?.id ?? null}
        fileName={errorsFor?.fileName}
        failedCount={errorsFor?.failed}
      />
    </React.Fragment>
  );
};

/* ────────────── Sub-components ────────────── */

function StatCard({
  icon,
  label,
  value,
  highlight = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  highlight?: boolean;
}) {
  return (
    <div className={[
      "rounded-lg border px-4 py-3 transition-colors",
      highlight
        ? "border-amber-200 bg-amber-50/50"
        : "border-gray-200 bg-white",
    ].join(" ")}>
      <div className={["flex items-center gap-1.5", highlight ? "text-amber-700" : "text-gray-400"].join(" ")}>
        {icon}
        <span className="text-xs font-medium">{label}</span>
      </div>
      <div className="mt-1.5 flex items-baseline gap-2">
        <span className="text-2xl font-semibold text-gray-900 tabular-nums">
          {value.toLocaleString()}
        </span>
        {highlight && value > 0 && (
          <span className="text-[11px] text-amber-700">needs action</span>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: "activated" | "pending" | "failed" }) {
  if (status === "activated") {
    return <Badge className="bg-gray-100 text-gray-700 hover:bg-gray-100 text-[10px] font-medium px-1.5 py-0 h-4">Activated</Badge>;
  }
  if (status === "pending") {
    return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px] font-medium px-1.5 py-0 h-4">Pending</Badge>;
  }
  return <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[10px] font-medium px-1.5 py-0 h-4">Failed</Badge>;
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full border border-gray-200 bg-gray-50 mb-4">
        <Inbox className="h-6 w-6 text-gray-400" />
      </div>
      <h3 className="text-base font-semibold text-gray-900 mb-1">No imported lists yet</h3>
      <p className="max-w-sm text-sm text-gray-500">
        Previously uploaded CSVs will appear here. Upload your first file to get started.
      </p>
    </div>
  );
}

export default ImportedLeadsList;
